import { Schedule, PlayArrow, Assignment } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { styled, alpha } from '@mui/material/styles';

// Utility function to shorten numbers
const fShortenNumber = (num: number): string => {
  if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
  return num.toString();
};

// Styled component
const RootStyle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color?: string }>(({ color }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  padding: '16px 20px',
  background: 'rgba(255, 255, 255, 0.01)',
  border: '1px solid rgba(255, 255, 255, 0.04)',
  borderRadius: '20px',
  minHeight: 110,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'default',
  overflow: 'hidden',
  height: '100%',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.03)',
    borderColor: alpha(color || '#fff', 0.15),
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px ${alpha('#000', 0.3)}`,
    '& .metric-icon-bg': {
      transform: 'scale(1.1) rotate(5deg)',
      background: alpha(color || '#fff', 0.1),
    },
  },
}));

interface FollowUpCardProps {
  title: string;
  remaining: number;
  completed?: number;
  icon?: 'schedule' | 'play' | 'assignment';
  singleMetric?: boolean;
  leftLabel?: string;
  rightLabel?: string;
  color?: string; // Add color for left border
}

export default function FollowUpCard({
  title,
  remaining,
  completed,
  icon = 'schedule',
  singleMetric = false,
  leftLabel = 'Remaining',
  rightLabel = 'Completed',
  color,
}: FollowUpCardProps) {
  const getIcon = () => {
    switch (icon) {
      case 'play':
        return PlayArrow;
      case 'assignment':
        return Assignment;
      default:
        return Schedule;
    }
  };

  const IconComponent = getIcon();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      style={{ height: '100%' }}
    >
      <RootStyle color={color}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
            gap: 1.5,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            className='metric-icon-bg'
            sx={{
              width: 32,
              height: 32,
              borderRadius: '10px',
              bgcolor: alpha(color || '#fff', 0.05),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
              transition: 'all 0.4s ease',
            }}
          >
            <IconComponent sx={{ fontSize: '1.1rem' }} />
          </Box>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.35)',
              fontWeight: 800,
              fontSize: '0.65rem',
              textTransform: 'uppercase',
              letterSpacing: '1.2px',
              fontFamily: 'Lato, sans-serif',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {singleMetric ? (
            <Typography
              className='metric-value'
              sx={{
                fontWeight: 900,
                fontSize: '1.9rem',
                color: '#fff',
                letterSpacing: '-0.5px',
                lineHeight: 1,
                fontFamily: 'Lato, sans-serif',
              }}
            >
              {fShortenNumber(remaining)}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box>
                <Typography
                  className='metric-value'
                  sx={{
                    fontWeight: 900,
                    fontSize: '1.5rem',
                    color: '#fff',
                    lineHeight: 1,
                    fontFamily: 'Lato, sans-serif',
                  }}
                >
                  {fShortenNumber(remaining)}
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.2)',
                    fontWeight: 800,
                    fontSize: '0.55rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mt: 0.4,
                    fontFamily: 'Lato, sans-serif',
                  }}
                >
                  {leftLabel}
                </Typography>
              </Box>

              <Box sx={{ width: 1, height: 24, bgcolor: 'rgba(255, 255, 255, 0.06)' }} />

              <Box>
                <Typography
                  className='metric-value'
                  sx={{
                    fontWeight: 900,
                    fontSize: '1.5rem',
                    color: alpha('#fff', 0.9),
                    lineHeight: 1,
                    fontFamily: 'Lato, sans-serif',
                  }}
                >
                  {fShortenNumber(completed || 0)}
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.2)',
                    fontWeight: 800,
                    fontSize: '0.55rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mt: 0.4,
                    fontFamily: 'Lato, sans-serif',
                  }}
                >
                  {rightLabel}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </RootStyle>
    </motion.div>
  );
}
